import {
    EndPageEnum,
    EventTypeEnum,
    type QuoteModel,
    type GetPremiumResponseModel,
    type GetCustomerResponseModel,
    type GetQuoteResponseModel,
    type RatesResponseModel,
} from "@/models";
import { defineStore } from "pinia";
import {
    useCustomerStore,
    useDataLayerStore,
    useGlobalStore,
    useJourneyStore,
} from "@/stores";
import dayjs from "dayjs";
import * as sentry from "@sentry/vue";
import { get } from "@/utils/axios-utils";

export const useQuoteStore = defineStore("quote", {
    state: (): QuoteModel => {
        return {
            priceDate: new Date(),
            myQuote: -1,
            myDeal: -1,
            quoteID: "",
            quoteSecret: "",
            premiumID: "",
            premiumSecret: "",
            availableDeals: [],
            quoteContinued: false,
        };
    },
    getters: {
        testQuote: (): boolean => {
            return window.location.href.includes("testquote=y");
        },
        mySA: (state): number => {
            if (
                state.availableDeals.length > 0 &&
                state.myDeal >= 0 &&
                state.availableDeals[state.myDeal]
            ) {
                return state.availableDeals[state.myDeal].insuredAmount;
            }
            return 0;
        },
        myPPM: (state): number => {
            if (
                state.availableDeals.length > 0 &&
                state.myDeal >= 0 &&
                state.availableDeals[state.myDeal]
            ) {
                return state.availableDeals[state.myDeal].monthlyCost;
            }
            return 0;
        },
        minPPM: (state): number => {
            if (state.availableDeals.length > 0) {
                return state.availableDeals[0].monthlyCost;
            }
            return 0;
        },
        maxPPM: (state): number => {
            if (state.availableDeals.length > 0) {
                return state.availableDeals[state.availableDeals.length - 1]
                    .monthlyCost;
            }
            return 0;
        },
        maxSA: (state): number => {
            if (state.availableDeals.length > 0) {
                return state.availableDeals[state.availableDeals.length - 1]
                    .insuredAmount;
            }
            return 0;
        },
        myPaymentPeriod(): number {
            return Math.ceil(this.mySA / this.myPPM);
        },
        myPaymentPeriodYears(): number {
            return this.myPaymentPeriod / 12;
        },
        myPaymentPeriodMonths(): number {
            return this.myPaymentPeriod % 12;
        },
        myBreakEvenAge(): string {
            let monthsToBreakEven = dayjs()
                .add(this.myPaymentPeriodYears, "year")
                .add(this.myPaymentPeriodMonths, "month")
                .diff(
                    dayjs(
                        `${this.customer.dobYear}-${this.customer.dobMonth}-${this.customer.dobDate}`,
                        "YYYY-MM-DD",
                        true
                    ),
                    "months"
                );
            const yearsToBreakEven = Math.floor(monthsToBreakEven / 12);
            monthsToBreakEven = monthsToBreakEven % 12;
            let compiledString = `${yearsToBreakEven} years`;
            if (monthsToBreakEven > 0) {
                compiledString = `${compiledString} and ${monthsToBreakEven} months`;
            }
            return compiledString;
        },
        dataLayer: () => {
            return useDataLayerStore();
        },
        customer: () => {
            return useCustomerStore();
        },
        journey: () => {
            return useJourneyStore();
        },
        global: () => {
            return useGlobalStore();
        },
    },
    actions: {
        async getDeals(questComplete?: string): Promise<void> {
            if (questComplete) {
                this.dataLayer.triggerEvent(
                    EventTypeEnum.QuestComplete,
                    questComplete
                );
            }
            if (this.customer.haveEnoughToGetDeals) {
                // Step the user back if they make changes to the selected rate after they have gone through the journey
                if (this.global.step > 1) {
                    this.global.step = 1;
                }
                this.myDeal = -1;
                this.myQuote = -1;
                try {
                    const { data } = await get<RatesResponseModel>(
                        `${this.global.env.API_URL}/api/rates/overfifties`,
                        undefined,
                        {
                            campaign: this.journey.campaign,
                            age: this.customer.age,
                            agency: this.journey.agencyNumber,
                            smoker: this.customer.smoker,
                            testQuote: this.testQuote || undefined,
                            scheme: this.global.env.ASANTO_TARGET,
                        }
                    );
                    this.availableDeals = data.rates;
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                } catch (error: any) {
                    sentry.captureException(error);
                    this.dataLayer.pageNumber = 8;
                    this.dataLayer.pageError = {
                        errorType: "Page",
                        errorCode: error.response?.status || 500,
                        errorMessage: error.message,
                    };
                    this.dataLayer.errors.push(error.message);
                    this.dataLayer.triggerEvent(EventTypeEnum.ErrorEvent);
                    this.global.hideForm(EndPageEnum.ErrorPage);
                }
            }
        },
        preloadQuoteData(quote: GetQuoteResponseModel): void {
            this.myDeal = this.availableDeals.findIndex(
                (deal) => deal.monthlyCost === quote.ppmamount
            );
            this.myQuote = this.myDeal;
        },
        async preloadFromPremium(id: string, secret: string): Promise<void> {
            const { data: premiumResponse } =
                await get<GetPremiumResponseModel>(
                    `${this.global.env.API_URL}/api/v2/premiums/overfifties/${id}`,
                    secret
                );

            this.quoteContinued = true;
            this.premiumID = id;
            this.premiumSecret = premiumResponse.premiumSecret;
            this.quoteID = premiumResponse.quoteID;
            this.quoteSecret = premiumResponse.quoteSecret;
            this.customer.customerID = premiumResponse.customerID;
            this.customer.customerSecret = premiumResponse.customerSecret;

            const { data: customerResponse } =
                await get<GetCustomerResponseModel>(
                    `${this.global.env.API_URL}/api/v2/customers/${this.customer.customerID}`,
                    this.customer.customerSecret
                );

            this.customer.preloadCustomerData(customerResponse);

            const { data: quoteResponse } = await get<GetQuoteResponseModel>(
                `${this.global.env.API_URL}/api/v2/quotes/overfifties/${this.quoteID}`,
                this.quoteSecret
            );

            this.journey.preloadJourneyData(quoteResponse);
            await this.getDeals();

            this.preloadQuoteData(quoteResponse);
        },
    },
});
